import React, {useContext, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import Form from 'components/common/Form';
import Select from 'components/common/Form/Select';
import Datepicker from 'components/common/Form/Datepicker';
import Button, {ButtonStyles} from 'components/common/Form/Button';
import PhoneInput from 'components/common/Form/PhoneInput';
import Input from 'components/common/Form/Input';

import UserRoles, {isModerator, isRespondent, isTranslator} from 'constants/UserRoles';
import GENDERS from 'constants/GenderInformation';
import {convertToOptions, convertToValue} from 'helpers/SelectUtils';
import {TranslateIndustries} from 'constants/BusinessInformation';

import {UserContext} from 'context/UserContext';

import style from 'components/Welcome/Form/Form.module.scss';
import MultiSwitch from 'components/common/Form/MultiSwitch/MultiSwitch';
import {
    getTranslatedName,
    NATIONALITIES,
    TRANSLATE_LANGUAGES,
    TRANSLATE_NATIONALITIES,
} from 'components/common/listConstants';
import MultiSelect from 'components/common/Form/MultiSelect/MultiSelect';
import _ from 'lodash';
import OptionSlider from 'components/common/Form/OptionSlider/OptionSlider';
import YearsOfExperience from 'constants/YearsOfExperience';
import cn from 'classnames';
import QuestionMarkTooltip from 'components/common/QuestionMarkTooltip/QuestionMarkTooltip';

export default function ClientPersonalBasicForm({onChange, onNextStep, data, role, errors, validate, isModal}) {
    const {
        workExperience,
        workIndustry,
        birthday,
        phone,
        languages,
        gender,
        phoneCountryCode,
        nativeCountry,
        firstName,
        lastName,
    } = data;
    const {validatePhoneNumberResult, clearPhoneNumberValidation} = useContext(UserContext);
    useEffect(() => {
        if (validatePhoneNumberResult) clearPhoneNumberValidation();
    }, [phone, phoneCountryCode]);

    const isCompanyAdmin = role === UserRoles.companyAdmin;
    const isTranslatorOrModerator = isModerator(role) || isTranslator(role);
    const isRespondentUser = isRespondent(role);

    const MAX_AGE = 110;
    const MIN_AGE = 18;
    const MAX_NAME_LENGTH = 64;

    const {t} = useTranslation();

    return (
        <div className={style.profileFormContainer}>
            <Form className={cn(style.form, {[style.wideForm]: isTranslatorOrModerator, [style.modal]: isModal})}>
                <div className={style.inlineFieldsContainer}>
                    <Input
                        label={t('welcome:form.labels.firstname')}
                        placeholder={t('welcome:form.placeholders.firstname')}
                        labelClassName={style.labelClassName}
                        value={firstName}
                        onChange={onChange.bind(this, 'firstName')}
                        error={
                            t(errors.firstName) ||
                            (firstName && firstName.trim().length === 64 ? t('welcome:errors.firstNameLength') : '')
                        }
                        errorAbsolutePosition
                        withFocus
                        onBlur={validate.bind(this, data, 'firstName')}
                        firstCapitalized
                        maxLength={MAX_NAME_LENGTH}
                    />
                    <Input
                        label={t('welcome:form.labels.lastname')}
                        placeholder={t('welcome:form.placeholders.lastname')}
                        labelClassName={style.labelClassName}
                        value={lastName}
                        onChange={onChange.bind(this, 'lastName')}
                        error={
                            t(errors.lastName) ||
                            (lastName && lastName.trim().length === 64 ? t('welcome:errors.lastNameLength') : '')
                        }
                        errorAbsolutePosition
                        onBlur={validate.bind(this, data, 'lastName')}
                        firstCapitalized
                        maxLength={MAX_NAME_LENGTH}
                    />
                </div>
                <div className={style.inlineFieldsContainer}>
                    <Datepicker
                        label={t('welcome:form.labels.date')}
                        value={birthday}
                        onChange={onChange.bind(this, 'birthday')}
                        error={t(errors.birthday)}
                        name='birthday'
                        maxDate={new Date().setFullYear(new Date().getFullYear() - MIN_AGE)}
                        minDate={new Date().setFullYear(new Date().getFullYear() - MAX_AGE)}
                        withDateFormatToUTC
                        labelClassName={style.labelClassName}
                        id='birthday'
                        onChangeField={_.noop}
                        customInputClassName={style.withoutMarginBottom}
                        onBlur={validate.bind(this, data, 'birthday')}
                    />
                    <PhoneInput
                        name='phone'
                        label={t('welcome:form.labels.phone')}
                        value={phone}
                        phoneCountryCode={phoneCountryCode}
                        onChange={onChange}
                        error={t(errors.phone)}
                        customTooltipText={isRespondentUser && t('tooltips:phone')}
                        labelClassName={style.labelClassName}
                        className={style.withoutMarginBottom}
                        onBlur={validate.bind(this, data, 'phone')}
                    />
                </div>
                <div className={style.inlineFieldsContainer}>
                    {isRespondentUser && (
                        <div className={style.gender}>
                            <span>{t('welcome:form.labels.gender')}</span>
                            <MultiSwitch
                                options={convertToOptions(GENDERS)}
                                value={gender}
                                onChange={onChange.bind(this, 'gender')}
                            />
                        </div>
                    )}
                </div>
                <div className={style.inlineFieldsContainer}>
                    <Select
                        label={t('welcome:form.labels.nationality')}
                        placeholder={t('welcome:form.placeholders.nationality')}
                        options={TRANSLATE_NATIONALITIES(NATIONALITIES, t)}
                        value={nativeCountry}
                        onChange={onChange.bind(this, 'nativeCountry')}
                        error={t(errors.nativeCountry)}
                        labelClassName={style.labelClassName}
                        onBlur={validate.bind(this, data, 'nativeCountry')}
                    />
                    {isCompanyAdmin && (
                        <Select
                            label={t('welcome:form.labels.workIndustry')}
                            placeholder={t('welcome:form.placeholders.workIndustry')}
                            options={convertToOptions(TranslateIndustries(t))}
                            value={convertToValue(workIndustry, TranslateIndustries(t))}
                            onChange={onChange.bind(this, 'workIndustry')}
                            error={t(errors.workIndustry)}
                            labelClassName={style.labelClassName}
                            optionExtraClassName={style.extraOptionClass}
                        />
                    )}
                </div>
                {!isTranslator(role) && !isCompanyAdmin && (
                    <div className={style.inlineFieldsContainer}>
                        <MultiSelect
                            label={t('welcome:form.labels.language')}
                            placeholder={t('welcome:form.placeholders.language')}
                            options={TRANSLATE_LANGUAGES(t)}
                            selected={languages}
                            onChange={onChange.bind(this, 'languages')}
                            error={t(errors.languages)}
                            labelClassName={style.labelClassName}
                            optionsClassName={style.optionsClassName}
                            onBlur={validate.bind(this, data, 'languages')}
                            halfSize
                            getName={getTranslatedName}
                        />
                        <div className={cn(style.markWrapper, style.halfSize)}>
                            <QuestionMarkTooltip
                                text={t('welcome:form.tooltips.languages')}
                                classes={{tooltip: style.tooltip}}
                                markClasses={style.questionMarkIcon}
                            />
                        </div>
                    </div>
                )}
                {isTranslatorOrModerator && (
                    <div className={style.inlineFieldsContainer}>
                        <OptionSlider
                            options={YearsOfExperience}
                            className={style.experience}
                            label={
                                isModerator(role)
                                    ? t('welcome:form.labels.moderExperience')
                                    : t('welcome:form.labels.transExperience')
                            }
                            width={41.5}
                            onChange={onChange.bind(this, 'workExperience')}
                            value={workExperience}
                            error={t(errors.workExperience)}
                            onBlur={validate.bind(this, data, 'workExperience')}
                        />
                    </div>
                )}
                <div className={style.buttonWrapper}>
                    <Button text={t('welcome:buttons.continue')} btnStyle={ButtonStyles.primary} onClick={onNextStep} />
                </div>
            </Form>
        </div>
    );
}
